/**
 * After joinChit / pay APIs return, open Razorpay checkout (or follow PhonePe link).
 * Landing previously only looked for paymentLink — Razorpay returns orderId + keyId.
 */

export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-checkout-js') && window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.getElementById('razorpay-checkout-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(Boolean(window.Razorpay)));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(Boolean(window.Razorpay));
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/** Normalize API body whether fields are under `result` or flat on `data`. */
export function extractPaymentInitResult(res) {
  const data = res?.data;
  if (!data || typeof data !== 'object') return null;
  const nested = data.result;
  if (nested && typeof nested === 'object' && (nested.razorpayOrderId || nested.paymentLink)) {
    return nested;
  }
  if (data.razorpayOrderId || data.paymentLink) {
    return data;
  }
  return nested && typeof nested === 'object' ? nested : null;
}

/**
 * @param {object} result — payment init payload
 * @param {{ name?: string, description?: string, verifyUrl: string, getAuthHeader: () => object, onVerifying?: () => void }} options
 */
export async function completePaymentCheckout(result, options) {
  if (!result || typeof result !== 'object') {
    throw new Error('Payment response was empty. Please try again.');
  }

  if (result.razorpayOrderId) {
    if (!result.razorpayKeyId) {
      throw new Error(
        'Razorpay key missing. Ask the company admin to reconnect Payments.',
      );
    }
    const loaded = await loadRazorpayScript();
    if (!loaded || !window.Razorpay) {
      throw new Error(
        'Failed to load Razorpay checkout. Check network / ad blockers.',
      );
    }

    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: result.razorpayKeyId,
        amount: result.amount,
        currency: result.currency || 'INR',
        name: options?.name || 'ChitBid',
        description: options?.description || 'Join chit payment',
        order_id: result.razorpayOrderId,
        handler: async (response) => {
          try {
            options?.onVerifying?.();
            const verifyRes = await fetch(options.verifyUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(options.getAuthHeader?.() || {}),
              },
              body: JSON.stringify({
                ...response,
                transaction_id: result.transaction_id || '',
              }),
            });
            const body = await verifyRes.json().catch(() => ({}));
            if (!verifyRes.ok || verifyRes.status === 400) {
              reject(
                new Error(body?.msg || 'Payment verification failed'),
              );
              return;
            }
            resolve(body);
          } catch {
            reject(new Error('Payment verification failed'));
          }
        },
        modal: {
          ondismiss: () => {
            reject(new Error('Payment cancelled'));
          },
        },
      });
      rzp.on('payment.failed', () => {
        reject(new Error('Payment failed'));
      });
      try {
        rzp.open();
      } catch {
        reject(new Error('Could not open Razorpay checkout'));
      }
    });
  }

  const paymentLink =
    result.paymentLink || result.url || result.short_url || null;
  if (paymentLink) {
    window.location.href = paymentLink;
    return null;
  }

  throw new Error(
    'No payment method available. Company payment gateway may not be connected.',
  );
}

/** True when checkout cannot start because company gateway is missing/incomplete. */
export function isPaymentSetupCheckoutError(message) {
  return /razorpay key missing|no payment method available|payment gateway may not be connected|reconnect payments/i.test(
    String(message || ''),
  );
}
